import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { get, set } from '@totalsoft/change-tracking-react';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import { emptyString, emptyArray } from 'constants/defaultValues';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const CascoPolicyDataComponent = ({ policyDataLens, paymentTypes, validation, readOnly }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditPolicyDataCard = useAuthorization(AuthorizationCodes.hasRightToEditPolicyDataCard, documentTypeId, documentStateId);
  const { t } = useTranslation();

  return (
    <>
      <Grid item xs={12} md={6} lg={3}>
        <TextField
          id={'Policy_InsurerName'}
          fullWidth
          value={policyDataLens.insurerName |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.InsurerName')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          id={'Policy_InsuranceAmount'}
          fullWidth
          isNumeric={true}
          value={policyDataLens.insuranceAmount |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.InsuranceAmountWithCurrency', {
            currency: policyDataLens.currencyCode |> get |> valueOrDefault(emptyString)
          })}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <TextField
          id={'Policy_Period'}
          fullWidth
          value={policyDataLens.period |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.Period')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <DateTime
          id={'Policy_StartDate'}
          fullWidth
          value={policyDataLens.startDate |> get}
          onChange={policyDataLens.startDate |> set}
          label={t('Offer.Edit.PolicyData.StartDate')}
          disabled={true}
          timeFormat={false}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <TextField
          id={'Policy_InsuranceDocumentNumber'}
          fullWidth
          value={policyDataLens.insuranceDocumentNumber |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.InsuranceDocumentNumber')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Autocomplete
          id='Policy_PaymentType'
          label={t('Offer.Edit.PolicyData.PaymentType')}
          fullWidth
          required
          isClearable={false}
          isSearchable={true}
          valueKey='id'
          labelKey='name'
          value={policyDataLens.paymentTypeId |> get}
          onChange={policyDataLens.paymentTypeId |> set}
          options={paymentTypes ? paymentTypes : emptyArray}
          disabled={readOnly('PolicyData.PaymentType', hasRightToEditPolicyDataCard)}
          simpleValue={true}
          error={!isValid(validation.paymentTypeId)}
          helperText={getErrors(validation.paymentTypeId)}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <DateTime
          id='Policy_PaymentDocumentDate'
          label={t('Offer.Edit.PolicyData.PaymentDocumentDate')}
          fullWidth
          required
          onChange={policyDataLens.paymentDocumentDate |> set}
          value={policyDataLens.paymentDocumentDate |> get}
          timeFormat={false}
          isClearable={!readOnly('PolicyData.PaymentDocumentDate')}
          disabled={readOnly('PolicyData.PaymentDocumentDate', hasRightToEditPolicyDataCard)}
          error={!isValid(validation.paymentDocumentDate)}
          helperText={getErrors(validation.paymentDocumentDate)}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <TextField
          id={'Policy_PaymentDocumentSeries'}
          fullWidth
          required
          onChange={policyDataLens.paymentDocumentSeries |> set}
          value={policyDataLens.paymentDocumentSeries |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.PaymentDocumentSeries')}
          disabled={readOnly('PolicyData.PaymentDocumentSeries', hasRightToEditPolicyDataCard)}
          error={!isValid(validation.paymentDocumentSeries)}
          helperText={getErrors(validation.paymentDocumentSeries)}
          inputProps={{
            maxLength: 50
          }}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <TextField
          id={'Policy_PaymentDocumentNumber'}
          fullWidth
          required
          onChange={policyDataLens.paymentDocumentNumber |> set}
          value={policyDataLens.paymentDocumentNumber |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.PaymentDocumentNumber')}
          disabled={readOnly('PolicyData.PaymentDocumentNumber', hasRightToEditPolicyDataCard)}
          error={!isValid(validation.paymentDocumentNumber)}
          helperText={getErrors(validation.paymentDocumentNumber)}
          inputProps={{
            maxLength: 50
          }}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <TextField
          id={'Policy_FinancingContractNumber'}
          fullWidth
          value={policyDataLens.financingContractNumber |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.PolicyData.FinancingContractNumber')}
          disabled={true}
        />
      </Grid>
    </>
  );
};

CascoPolicyDataComponent.propTypes = {
  policyDataLens: PropTypes.object.isRequired,
  paymentTypes: PropTypes.array,
  validation: PropTypes.object,
  readOnly: PropTypes.func.isRequired
};

export default CascoPolicyDataComponent;
